// Talent Bank arithmetic and vocabulary, shared by the staff screen, the family screen and the
// report they both render. No React, no Firestore -- everything here is a pure function of its
// arguments, which is what makes it safe to share across three files.

// Chart series colours. Literal on purpose -- a series has to be distinguishable from the other
// series, which is a different job from matching the brand.
export const COLOR_PALETTE = ['#C81042', '#325CA2', '#00AA6C', '#D6B845', '#6B2D42', '#9C6ADE'];

export const GRADE_SCALE = {
  '5**': 7, '5*': 6, '5': 5, 'A': 5,
  '4': 4, 'B': 4,
  '3': 3, 'C': 3,
  '2': 2, 'D': 2,
  '1': 1, 'E': 1,
  'U': 0, 'F': 0,
};
export const gradeToValue = (g) => {
  if (g === null || g === undefined || g === '') return null;
  const v = GRADE_SCALE[String(g).trim().toUpperCase()];
  return v === undefined ? null : v;
};
export const valueToGradeLabel = (v) => {
  if (v >= 7) return '5**';
  if (v >= 6) return '5*';
  if (v >= 5) return '5/A';
  if (v >= 4) return '4/B';
  if (v >= 3) return '3/C';
  if (v >= 2) return '2/D';
  if (v >= 1) return '1/E';
  return 'U/F';
};

// Fixed heuristic: subjects treated as "core" for the Threats quadrant of the SWOT summary.
export const CORE_SUBJECT_KEYWORDS = ['english', 'chinese', 'math'];

// --- OLE Record helpers ---
export const parseTimeToMinutes = (t) => {
  if (!t) return null;
  const [h, m] = String(t).split(':').map(Number);
  if (Number.isNaN(h) || Number.isNaN(m)) return null;
  return h * 60 + m;
};
export const computeActivityHours = (start, end) => {
  const s = parseTimeToMinutes(start);
  const e = parseTimeToMinutes(end);
  if (s === null || e === null || e <= s) return 0;
  return (e - s) / 60;
};
export const oleBilingualLabel = (item) => item ? (item.nameZh ? `${item.nameEn || item.code} (${item.nameZh})` : (item.nameEn || item.code)) : '';
// Synthetic "nature" key for the aggregate All-natures total line, kept out of the real nature code space.
export const ALL_NATURES_KEY = '__ALL__';

// Net conduct balance, by school year and overall, worked out from the records themselves.
//
// Staff read the same numbers from metadata/student_credit_balances, which is an aggregate a rule
// cannot hand back one key of -- so a family gets them derived from the conduct records they are
// already allowed to read. Same arithmetic: the aggregate document is itself just balanceChange
// summed and grouped by year.
export const deriveCreditBalances = (creditRecords) => {
  const byYear = {};
  creditRecords.forEach((r) => {
    const yr = r.schoolYear || '';
    if (!yr) return;
    byYear[yr] = (byYear[yr] || 0) + (Number(r.balanceChange) || 0);
  });
  return {
    yearly: Object.keys(byYear)
      .sort((a, b) => b.localeCompare(a))
      .map((yr) => ({ year: yr, balance: byYear[yr] })),
    lifetime: creditRecords.reduce((sum, r) => sum + (Number(r.balanceChange) || 0), 0),
  };
};

// The same two numbers pulled out of the staff aggregate for one student.
export const creditBalancesFromAggregate = (balancesDoc, sid) => {
  const yearly = (balancesDoc || {}).yearly || {};
  return {
    yearly: Object.keys(yearly)
      .filter((yr) => sid in (yearly[yr] || {}))
      .sort((a, b) => b.localeCompare(a))
      .map((yr) => ({ year: yr, balance: yearly[yr][sid] || 0 })),
    lifetime: ((balancesDoc || {}).lifetime || {})[sid] || 0,
  };
};
